import React, { useState } from 'react';
import type { FormField, OptionLayout, ThemeConfig } from '../../../types/forms';
import { normalizeFieldOption } from '../../../types/forms';

const LAYOUT_CLASSES: Record<OptionLayout, string> = {
  vertical: 'space-y-3',
  horizontal: 'flex flex-wrap gap-3',
  'grid-2': 'grid grid-cols-1 md:grid-cols-2 gap-3',
  'grid-3': 'grid grid-cols-1 md:grid-cols-3 gap-3',
};

const LETTER_KEYS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

interface RadioFieldProps {
  field: FormField;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
  themeConfig?: ThemeConfig;
}

export const RadioField: React.FC<RadioFieldProps> = ({
  field,
  value = '',
  onChange,
  error,
  disabled,
  themeConfig,
}) => {
  const optionLayout = (field.config?.optionLayout as OptionLayout) || 'vertical';
  const allowOther = !!field.config?.allowOther;
  const isOtherSelected = allowOther && value !== '' && !field.options?.some(o => normalizeFieldOption(o).value === value);
  const [otherText, setOtherText] = useState(isOtherSelected ? value : '');

  const accentColor = themeConfig?.buttonBgColor || '#3b82f6';
  const textColor = themeConfig?.textColor || '#ffffff';

  return (
    <div className={LAYOUT_CLASSES[optionLayout] || 'space-y-3'}>
      {field.options?.map((rawOption, index) => {
        const opt = normalizeFieldOption(rawOption);
        const isSelected = value === opt.value;
        const letter = LETTER_KEYS[index] || '';

        return (
          <label
            key={opt.value}
            className={`
              flex items-center gap-3 cursor-pointer group
              w-full min-w-0 min-h-13 px-4 py-3 rounded-xl border
              transition-all duration-200
              touch-manipulation
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            style={{
              borderColor: isSelected ? accentColor : error ? '#ef4444' : 'rgba(115,115,115,0.5)',
              backgroundColor: isSelected ? `${accentColor}15` : 'transparent',
            }}
          >
            <div className="relative shrink-0">
              <input
                type="radio"
                name={field.id}
                value={opt.value}
                checked={isSelected}
                onChange={() => onChange?.(opt.value)}
                disabled={disabled}
                className="sr-only peer"
              />
              <div
                className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-semibold transition-all duration-200"
                style={{
                  borderColor: isSelected ? accentColor : 'rgba(115,115,115,0.6)',
                  backgroundColor: isSelected ? accentColor : 'transparent',
                  color: isSelected ? '#ffffff' : textColor,
                }}
              >
                {letter}
              </div>
            </div>
            <span className="text-base min-w-0 break-words" style={{ color: textColor }}>{opt.label}</span>
          </label>
        );
      })}

      {allowOther && (
        <label
          className={`
            flex items-center gap-3 cursor-pointer group
            w-full min-h-13 px-4 py-3 rounded-xl border
            transition-all duration-200
            touch-manipulation
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{
            borderColor: isOtherSelected ? accentColor : error ? '#ef4444' : 'rgba(115,115,115,0.5)',
            backgroundColor: isOtherSelected ? `${accentColor}15` : 'transparent',
          }}
        >
          <div className="relative shrink-0">
            <input
              type="radio"
              name={field.id}
              value="__other__"
              checked={isOtherSelected}
              onChange={() => onChange?.(otherText)}
              disabled={disabled}
              className="sr-only peer"
            />
            <div
              className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-semibold transition-all duration-200"
              style={{
                borderColor: isOtherSelected ? accentColor : 'rgba(115,115,115,0.6)',
                backgroundColor: isOtherSelected ? accentColor : 'transparent',
                color: isOtherSelected ? '#ffffff' : textColor,
              }}
            >
              {LETTER_KEYS[field.options?.length || 0]}
            </div>
          </div>
          <span className="text-base min-w-0 break-words" style={{ color: textColor }}>Outro</span>
          {isOtherSelected && (
            <input
              type="text"
              value={otherText}
              onChange={(e) => { setOtherText(e.target.value); onChange?.(e.target.value); }}
              placeholder="Digite sua resposta..."
              disabled={disabled}
              className="flex-1 bg-transparent border-b border-neutral-600 text-base outline-none focus:border-primary-500 px-1 py-0.5"
              style={{ color: textColor }}
            />
          )}
        </label>
      )}
    </div>
  );
};
